import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const eur = v => v == null ? "—" : `€${Math.round(v).toLocaleString("it-IT")}`;
const isPct = k => k === "ebitda_margin";
const fmtM = (k, v) => v == null ? "—" : (isPct(k) ? `${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%` : eur(v));
const TREND = { UP: <TrendingUp className="w-4 h-4 text-[#10B981]" />, DOWN: <TrendingDown className="w-4 h-4 text-[#DC2626]" />, FLAT: <Minus className="w-4 h-4 text-[#7A7788]" /> };

export default function AnalisiBilancio() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/analysis/multiperiod").then(r => setData(r.data)).catch(e => {
      setErr(String(formatApiError(e)).includes("upgrade_required") ? "locked" : "generic");
    });
  }, []);

  if (err === "locked") return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="ab-locked">
      <h1 className="text-3xl font-extrabold">Analisi Bilancio</h1>
      <p className="text-[#524F5E] mt-3">L'analisi multi-periodo con delta e trend è disponibile dal piano <b>PRO</b>.</p>
      <Link to="/pricing" className="ic-btn-primary inline-block mt-6" data-testid="ab-pricing">Vedi i piani</Link>
    </div>
  );
  if (err) return <div className="max-w-3xl mx-auto px-4 py-20 text-center">Errore di caricamento.</div>;
  if (!data) return <div className="max-w-6xl mx-auto px-4 py-20 text-center">Caricamento analisi…</div>;
  if (data.status === "INSUFFICIENT_HISTORY") return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="ab-empty">
      <h1 className="text-3xl font-extrabold">Analisi Bilancio</h1>
      <p className="text-[#524F5E] mt-3">{data.message} Esercizi con dati: {data.periods_available}.</p>
      <Link to="/report" className="ic-btn-primary inline-block mt-6" data-testid="ab-complete">Completa i dati</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="ab-page">
      <p className="text-xs uppercase tracking-widest text-[#FF6A24] font-bold">Analisi Bilancio</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Confronto multi-periodo</h1>
      <p className="text-sm text-[#7A7788] mt-2">Esercizi: {data.periods.join(" · ")} · Financial Engine v{data.engine_version}</p>

      <div className="ic-card overflow-x-auto mt-8 mb-10" data-testid="ab-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#7A7788] text-xs uppercase tracking-widest">
              <th className="p-3">Voce</th>
              {data.periods.map(p => <th key={p} className="p-3 text-right">{p.replace("fy_", "")}</th>)}
              <th className="p-3 text-right">Delta €</th>
              <th className="p-3 text-right">Delta %</th>
              <th className="p-3 text-right">Trend</th>
            </tr>
          </thead>
          <tbody>
            {data.metrics.map(m => {
              const lastD = m.deltas[m.deltas.length - 1] || {};
              return (
                <tr key={m.metric} className="border-t border-[#F4F4F7]" data-testid={`ab-row-${m.metric}`}>
                  <td className="p-3 font-semibold">{m.label}</td>
                  {m.values.map(v => <td key={v.period} className="p-3 text-right">{fmtM(m.metric, v.value)}</td>)}
                  <td className="p-3 text-right">{lastD.delta_abs != null && !isPct(m.metric) ? `${lastD.delta_abs > 0 ? "+" : ""}${eur(lastD.delta_abs)}` : "—"}</td>
                  <td className={`p-3 text-right font-semibold ${lastD.delta_pct > 0 ? "text-[#10B981]" : lastD.delta_pct < 0 ? "text-[#DC2626]" : ""}`}>
                    {lastD.delta_pct != null ? `${lastD.delta_pct > 0 ? "+" : ""}${lastD.delta_pct.toLocaleString("it-IT")}%` : "—"}
                  </td>
                  <td className="p-3 text-right"><span className="inline-flex justify-end" data-testid={`ab-trend-${m.metric}`}>{TREND[m.trend]}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.insights.length > 0 && (
        <div className="ic-card p-6 border-[#5424D5]/30" data-testid="ab-insights">
          <h2 className="font-bold text-lg mb-3">Analisi trend</h2>
          {data.insights.map((t, i) => (
            <p key={i} className="text-sm text-[#524F5E] mb-2 flex gap-2" data-testid={`ab-insight-${i}`}>
              <span className="mt-1.5 w-2 h-2 rounded-full bg-[#FF6A24] flex-shrink-0" />{t}
            </p>
          ))}
          <p className="text-[10px] text-[#7A7788] mt-3">Osservazioni su andamenti misurati, senza attribuzioni di causa non dimostrate.</p>
        </div>
      )}
    </div>
  );
}
